import type { Aspect, BackcountryMetadata, HikingDifficulty, UpdatePlaceData } from "~/lib/api";
import { AspectSelector } from "./AspectSelector";
import { BackcountryStats } from "./BackcountryStats";
import { DifficultySelector } from "~/components/place/DifficultySelector";
import { DistanceEditor } from "~/components/place/DistanceEditor";
import { DurationEditor } from "~/components/place/DurationEditor";
import { ElevationEditor } from "~/components/place/ElevationEditor";
import { InfoField } from "~/components/place/InfoField";
import { MonthSelector } from "./MonthSelector";
import { TrackUploader } from "~/components/place/TrackUploader";
import { PanelSection } from "~/components/ui/panel-section";

interface BackcountryInfoSectionProps {
  metadata: BackcountryMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
  track?: GeoJSON.LineString | null;
  onTrackUpload?: (file: File) => Promise<void>;
  onTrackDelete?: () => Promise<void>;
}

export function BackcountryInfoSection({
  metadata,
  canEdit,
  onUpdate,
  track,
  onTrackUpload,
  onTrackDelete,
}: BackcountryInfoSectionProps) {
  const hasContent =
    metadata?.difficulty ||
    metadata?.distance ||
    metadata?.elevation ||
    metadata?.duration ||
    metadata?.aspect?.length ||
    metadata?.bestMonths?.length ||
    track;

  if (!canEdit && !hasContent) return null;

  const updateMetadata = (updates: Partial<BackcountryMetadata>) => {
    onUpdate?.({ metadata: { ...metadata, ...updates } });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Route Info</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <>
              {onTrackUpload && <TrackUploader track={track} onUpload={onTrackUpload} onDelete={onTrackDelete} />}
              <InfoField label="Difficulty">
                <DifficultySelector
                  value={(metadata?.difficulty as HikingDifficulty) ?? null}
                  onChange={(difficulty) => updateMetadata({ difficulty: difficulty ?? undefined })}
                />
              </InfoField>
              <InfoField label="Distance">
                <DistanceEditor
                  value={metadata?.distance}
                  onChange={(distance) => updateMetadata({ distance: distance ?? undefined })}
                  canEdit
                />
              </InfoField>
              <InfoField label="Elevation">
                <ElevationEditor
                  value={metadata?.elevation}
                  onChange={(elevation) => updateMetadata({ elevation: elevation ?? undefined })}
                  canEdit
                />
              </InfoField>
              <InfoField label="Duration">
                <DurationEditor
                  value={metadata?.duration}
                  onChange={(duration) => updateMetadata({ duration: duration ?? undefined })}
                  canEdit
                />
              </InfoField>
              <InfoField label="Aspect">
                <AspectSelector
                  value={metadata?.aspect as Aspect[] | undefined}
                  onChange={(aspect) => updateMetadata({ aspect: aspect ?? undefined })}
                />
              </InfoField>
              <InfoField label="Best months">
                <MonthSelector
                  value={metadata?.bestMonths}
                  onChange={(bestMonths) => updateMetadata({ bestMonths: bestMonths ?? undefined })}
                />
              </InfoField>
            </>
          ) : (
            <BackcountryStats metadata={metadata} className="text-muted-foreground" />
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}
